import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "./Carousel";
import { IconPause, IconPlay } from "./icons";
import Layout from "./Layout";
import heroImage from "./assets/hero-image.png";
import realitySection from "./assets/reality-section.webp";
import commercialRealEstate from "./assets/gemini-real-estate.png";
import brokersFailPdf from "./assets/22-reasons-why-brokers-fail.pdf";

const EMAILJS_PUBLIC_KEY =
  process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "-PeFNIlQtyA7tkVIX";
const EMAILJS_SERVICE_ID =
  process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_z5wxydv";
const EMAILJS_TEMPLATE_ID =
  process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_40n6mx9";
const DOWNLOAD_POPUP_SUBMITTED_KEY = "landing_download_popup_submitted";

const Landing = () => {
  const [realityCarouselApi, setRealityCarouselApi] = useState(null);
  const [realityCarouselMobileApi, setRealityCarouselMobileApi] = useState(null);
  const [isRealityCarouselPaused, setIsRealityCarouselPaused] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [isDownloadSubmitting, setIsDownloadSubmitting] = useState(false);
  const [hasTriggeredPopup, setHasTriggeredPopup] = useState(false);
  const [isRealitySectionInView, setIsRealitySectionInView] = useState(false);
  const realitySectionRef = useRef(null);
  const hasRealityCarouselStartedRef = useRef(false);
  const [downloadForm, setDownloadForm] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const realityCardClass =
    "relative w-full max-w-[18rem] min-h-[210px] overflow-hidden rounded-[24px] border border-white/70 bg-white/40 backdrop-blur-2xl backdrop-saturate-200 text-[#201c16] px-8 py-10 flex items-center justify-center text-center ring-1 ring-white/35 before:pointer-events-none before:absolute before:inset-0 before:bg-[linear-gradient(130deg,rgba(255,255,255,0.85)_0%,rgba(255,255,255,0.35)_36%,rgba(255,255,255,0)_60%)] before:content-[''] after:pointer-events-none after:absolute after:inset-[2px] after:rounded-[22px] after:border after:border-white/35 after:content-['']";
  const realityCardTextClass =
    "relative z-10 font-heading leading-snug tracking-[0.04em] font-medium reality-card-text";
  const realityCards = [
    { id: "limited-scalability", lines: ["Limited", "Scalability"] },
    { id: "no-institutional-backing", lines: ["No Institutional", "Backing"] },
    { id: "inconsistent-deal-flow", lines: ["Inconsistent", "Deal Flow"] },
    { id: "growth-dependent", lines: ["Growth Dependent", "on Individuals"] },
    { id: "informal-process", lines: ["Informal", "Process"] },
    { id: "no-long-term-brand", lines: ["No Long-Term", "Brand Equity"] },
    { id: "no-system", lines: ["No System"] },
  ];
  const realityCardGroups = [
    realityCards.slice(0, 3),
    realityCards.slice(3, 6),
    realityCards.slice(6),
  ];
  const renderRealityCard = (lines, cardId) => (
    <div key={cardId} className={realityCardClass}>
      <p className={realityCardTextClass}>
        {lines.map((line, index) => (
          <span key={`${line}-${index}`} className="block">{line}</span>
        ))}
      </p>
    </div>
  );

  const handleDownloadChange = (event) => {
    const { name, value } = event.target;
    setDownloadForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const triggerDownload = () => {
    const downloadLink = document.createElement("a");
    downloadLink.href = brokersFailPdf;
    downloadLink.download = "22-reasons-why-brokers-fail.pdf";
    downloadLink.rel = "noopener";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleDownloadSubmit = async (event) => {
    event.preventDefault();
    if (isDownloadSubmitting) {
      return;
    }
    setIsDownloadSubmitting(true);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          name: downloadForm.name.trim(),
          email: downloadForm.email.trim(),
          phone: downloadForm.phone.trim(),
          source: "Landing popup - 22 reasons why brokers fail",
          page_url: typeof window !== "undefined" ? window.location.href : "",
        },
        {
          publicKey: EMAILJS_PUBLIC_KEY,
        }
      );
      if (typeof window !== "undefined") {
        window.localStorage.setItem(DOWNLOAD_POPUP_SUBMITTED_KEY, "true");
      }
    } catch (error) {
      console.error("[Landing] EmailJS failed for popup download form:", error);
    } finally {
      triggerDownload();
      setIsDownloadSubmitting(false);
    }

    setDownloadForm({
      name: "",
      email: "",
      phone: "",
    });
    setIsDownloadOpen(false);
  };

  const closeDownload = () => setIsDownloadOpen(false);

  useEffect(() => {
    if (hasTriggeredPopup || typeof window === "undefined") {
      return;
    }

    if (window.localStorage.getItem(DOWNLOAD_POPUP_SUBMITTED_KEY) === "true") {
      return;
    }

    const timerId = window.setTimeout(() => {
      setIsDownloadOpen(true);
      setHasTriggeredPopup(true);
    }, 30000);

    return () => window.clearTimeout(timerId);
  }, [hasTriggeredPopup]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!realitySectionRef.current) {
      return;
    }

    const sectionNode = realitySectionRef.current;
    const observer = new window.IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsRealitySectionInView(entry.isIntersecting && entry.intersectionRatio >= 0.25);
      },
      { threshold: [0, 0.25, 0.5] }
    );

    observer.observe(sectionNode);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isRealitySectionInView || hasRealityCarouselStartedRef.current) {
      return;
    }

    if (!realityCarouselApi && !realityCarouselMobileApi) {
      return;
    }

    realityCarouselApi?.scrollToStart?.();
    realityCarouselMobileApi?.scrollToStart?.();
    hasRealityCarouselStartedRef.current = true;
  }, [isRealitySectionInView, realityCarouselApi, realityCarouselMobileApi]);

  useEffect(() => {
    if (!realityCarouselApi) {
      return;
    }

    if (isRealityCarouselPaused || !isRealitySectionInView) {
      return;
    }

    const intervalMs =
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches
        ? 8000
        : 3600;

    const intervalId = window.setInterval(() => {
      realityCarouselApi.scrollNext();
    }, intervalMs);

    return () => window.clearInterval(intervalId);
  }, [realityCarouselApi, isRealityCarouselPaused, isRealitySectionInView]);

  useEffect(() => {
    if (!realityCarouselMobileApi) {
      return;
    }

    if (isRealityCarouselPaused || !isRealitySectionInView) {
      return;
    }

    const intervalId = window.setInterval(() => {
      realityCarouselMobileApi.scrollNext();
    }, 3600);

    return () => window.clearInterval(intervalId);
  }, [realityCarouselMobileApi, isRealityCarouselPaused, isRealitySectionInView]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const head = document.head || document.getElementsByTagName("head")[0];
    if (!head) {
      return;
    }

    const selector = 'link[data-lcp-preload="landing-hero"]';
    let preloadLink = head.querySelector(selector);
    if (!preloadLink) {
      preloadLink = document.createElement("link");
      preloadLink.setAttribute("data-lcp-preload", "landing-hero");
      preloadLink.setAttribute("rel", "preload");
      preloadLink.setAttribute("as", "image");
      preloadLink.setAttribute("href", heroImage);
      preloadLink.setAttribute("imagesizes", "100vw");
      preloadLink.setAttribute("fetchpriority", "high");
      head.appendChild(preloadLink);
    }
  }, []);

  return (
    <Layout>
      <section className="page-hero py-0">
        <div className="full-bleed h-auto md:h-screen animate-fade-in-up relative">
          <img
            src={heroImage}
            alt="Douglas & McCartney - A Structured Real Estate Advisory Platform"
            className="w-full h-auto md:h-full object-contain md:object-cover object-center"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            sizes="100vw"
          />
          {/* <p className="hero-network-line">India&rsquo;s Fastest Growing Network</p> */}
        </div>
      </section>

      <section className="section-shell section-band section-divider landing-intro">
        <div className="section-container sticky top-[12vh] md:top-[14vh] lg:top-[16vh] z-20">
          <div className="grid-12">
            <div className="lg:col-span-8 lg:col-start-3 text-center space-y-6 text-measure mx-auto">
              <p className="body-text text-muted-foreground font-semibold">
                Douglas & McCartney operates as a professional real estate advisory platform designed to support brokers and advisors through structure, systems, mentorship, and long-term brand alignment.
              </p>
              <p className="body-text text-muted-foreground font-semibold">
                Douglas & McCartney operates as a mandate-holding, distribution-led real estate organization.
              </p>
              <p className="body-text text-muted-foreground font-semibold">
                We connect developers, investors, and buyers through a structured partner network built on credibility, systems, compliance, and execution discipline.
              </p>
              <p className="body-text text-muted-foreground font-semibold">
                The platform is designed to help partners move beyond fragmented brokerage and build scalable, mandate-driven businesses.
              </p>
              <div className="pt-6">
                <Link to="/partnership-model" className="btn-outline inline-block">
                  Explore the Platform
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section ref={realitySectionRef} className="section-shell section-surface section-divider">
        <div className="section-container">
          <div className="section-header text-center">
            <h2 className="heading-section mb-6">The Reality of Real Estate Advisory</h2>
            <p className="body-text text-muted-foreground text-measure mx-auto">
              Across the market, capable brokers operate independently-relying on personal networks, inconsistent deal flow, and informal processes. While experience exists, structure often does not.
            </p>
          </div>

          <h3 className="heading-subsection text-center mb-12 reality-subtitle">
            Problem &rarr; Consequence &rarr; <span className="reality-subtitle-nowrap">Long-term Risk.</span>
          </h3>
        </div>

        <div className="section-container mt-12">
          <div className="relative overflow-hidden rounded-[28px] border border-border bg-secondary/30">
            <div
              className="absolute inset-0 bg-center bg-cover bg-no-repeat reality-carousel-bg"
              style={{ backgroundImage: `url(${realitySection})` }}
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 bg-[linear-gradient(140deg,rgba(18,18,18,0.45)_0%,rgba(18,18,18,0.2)_45%,rgba(18,18,18,0.35)_100%)]"
              aria-hidden="true"
            />
            <div className="relative z-10 py-10">
              <div className="md:hidden">
                <Carousel
                  opts={{ loop: true, align: "center" }}
                  setApi={setRealityCarouselMobileApi}
                  className="max-w-[1100px] mx-auto reality-carousel"
                >
                  <CarouselContent className="-ml-4">
                    {realityCards.map((card) => (
                      <CarouselItem key={card.id} className="py-4 mobile-carousel-item">
                        <div className="flex justify-center">
                          {renderRealityCard(card.lines, card.id)}
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="left-2 md:left-4 reality-carousel-control" />
                  <CarouselNext className="right-2 md:right-4 reality-carousel-control" />
                  <button
                    type="button"
                    className="reality-carousel-toggle"
                    onClick={() => setIsRealityCarouselPaused((current) => !current)}
                    aria-pressed={isRealityCarouselPaused}
                    aria-label={isRealityCarouselPaused ? "Play carousel" : "Pause carousel"}
                  >
                    {isRealityCarouselPaused ? (
                      <IconPlay className="h-4 w-4" />
                    ) : (
                      <IconPause className="h-4 w-4" />
                    )}
                    <span className="sr-only">{isRealityCarouselPaused ? "Play" : "Pause"}</span>
                  </button>
                </Carousel>
              </div>
              <div className="hidden md:block">
                <Carousel
                  opts={{ loop: true, align: "center" }}
                  setApi={setRealityCarouselApi}
                  className="max-w-[1100px] mx-auto reality-carousel"
                >
                  <CarouselContent className="-ml-4">
                    {realityCardGroups.map((group, groupIndex) => (
                      <CarouselItem key={`group-${groupIndex}`} className="py-4">
                        <div
                          className={
                            group.length === 1
                              ? "flex justify-center"
                              : "grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 place-items-center"
                          }
                        >
                          {group.map((card) => renderRealityCard(card.lines, card.id))}
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="left-2 md:left-4 reality-carousel-control" />
                  <CarouselNext className="right-2 md:right-4 reality-carousel-control" />
                  <button
                    type="button"
                    className="reality-carousel-toggle"
                    onClick={() => setIsRealityCarouselPaused((current) => !current)}
                    aria-pressed={isRealityCarouselPaused}
                    aria-label={isRealityCarouselPaused ? "Play carousel" : "Pause carousel"}
                  >
                    {isRealityCarouselPaused ? (
                      <IconPlay className="h-4 w-4" />
                    ) : (
                      <IconPause className="h-4 w-4" />
                    )}
                    <span className="sr-only">{isRealityCarouselPaused ? "Play" : "Pause"}</span>
                  </button>
                </Carousel>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell section-band section-divider">
        <div className="section-container text-center">
          <h2 className="heading-section mb-6">Built for Long-Term Professional Growth</h2>
          <p className="body-text text-muted-foreground text-measure mx-auto">
            Douglas & McCartney exists to bring structure, governance, and institutional thinking to real estate advisory practice. The platform enables professionals to operate independently while benefiting from defined systems, mentorship, and strategic alignment.
          </p>
        </div>
      </section>

      <section className="section-shell section-surface section-divider">
        <div className="section-container">
          <div className="section-header text-center">
            <h2 className="heading-section mb-6">How Douglas & McCartney Is Different</h2>
          </div>
          <div className="grid-12 items-center">
            <div className="image-frame lg:col-span-6">
              <img
                src={commercialRealEstate}
                alt="Commercial Real Estate"
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
            <div className="space-y-6 lg:col-span-6">
              {[
                { title: "Platform-Led Advisory", desc: "Built around systems and processes, not individual dependency." },
                { title: "Structured Operations", desc: "Clear frameworks that support consistency, governance, and scale." },
                { title: "Mentorship & Strategic Guidance", desc: "Access to experienced industry leadership aligned with long-term growth." }
              ].map((item) => (
                <div key={item.title} className="feature-card">
                  <h4 className="heading-subsection heading-mid mb-2">{item.title}</h4>
                  <p className="body-text text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="text-center mt-8">
            <Link to="/partnership-model" className="btn-outline inline-block">Learn More</Link>
          </div>
        </div>
      </section>

      <section className="section-shell section-band section-divider">
        <div className="section-container">
          <div className="section-header text-center">
            <h2 className="heading-section mb-6">Who This Platform Is For</h2>
          </div>
          <div className="grid-12">
            <div className="content-card lg:col-span-6">
              <h3 className="heading-subsection heading-mid mb-6">Designed for professionals who:</h3>
              <ul className="space-y-3">
                {[
                  "Want to build a serious advisory career",
                  "Value structure and credibility",
                  "Seek long-term brand support",
                  "Think in multi-year horizons"
                ].map((item) => (
                  <li key={item} className="body-text flex items-start gap-3">
                    <span className="text-accent">&rsaquo;</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="content-card lg:col-span-6">
              <h3 className="heading-subsection heading-mid mb-6">Not Designed for:</h3>
              <ul className="space-y-3 text-muted-foreground">
                {[
                  "Short-term income seekers",
                  "Informal, deal-only operators",
                  "Those unwilling to work within systems"
                ].map((item) => (
                  <li key={item} className="body-text flex items-start gap-3">
                    <span>&rsaquo;</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell section-surface section-divider">
        <div className="section-container text-center">
          <h2 className="heading-section mb-6">Start a Conversation</h2>
          <p className="body-text text-muted-foreground text-measure mx-auto mb-8">Explore a more structured approach to real estate advisory.</p>
          <Link to="/contact" className="btn-primary inline-block">
            Apply For Partnership
          </Link>
        </div>
      </section>

      {isDownloadOpen && (
        <div className="landing-modal-overlay" role="dialog" aria-modal="true" onClick={closeDownload}>
          <div className="landing-modal" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="landing-modal-close" aria-label="Close" onClick={closeDownload}>
              &times;
            </button>
            <h3 className="heading-subsection">Download "22 Reasons Why Brokers Fail"</h3>
            <p className="body-text text-muted-foreground mb-4">
              Enter your name, email, and number to access the free download.
            </p>
            <form onSubmit={handleDownloadSubmit} className="space-y-4">
              <div>
                <label htmlFor="download-name" className="body-small block mb-2">Full name *</label>
                <input
                  id="download-name"
                  name="name"
                  placeholder="Full name"
                  value={downloadForm.name}
                  onChange={handleDownloadChange}
                  required
                  className="landing-input"
                />
              </div>
              <div>
                <label htmlFor="download-email" className="body-small block mb-2">Email *</label>
                <input
                  id="download-email"
                  name="email"
                  type="email"
                  placeholder="Email"
                  value={downloadForm.email}
                  onChange={handleDownloadChange}
                  required
                  className="landing-input"
                />
              </div>
              <div>
                <label htmlFor="download-phone" className="body-small block mb-2">Phone number *</label>
                <input
                  id="download-phone"
                  name="phone"
                  type="tel"
                  placeholder="Phone number"
                  value={downloadForm.phone}
                  onChange={handleDownloadChange}
                  required
                  className="landing-input"
                />
              </div>
              <button type="submit" className="btn-primary w-full" disabled={isDownloadSubmitting}>
                {isDownloadSubmitting ? "Sending..." : "Get Free Download"}
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Landing;
